import { createContext, useContext } from 'react';
import { SEVERITY_DOT, SEVERITY_WORD, STATUS_DOT, STATUS_WORD } from '../../lib/security-checks.js';
import { REF_FRAMEWORK_NAMES, refLabel, refList } from '../../lib/control-refs.js';

// Small pieces the Security page sections share (ENTRAID-347 redesign). Status and severity are a dot plus a word in
// ink: color lives only in the dot, never in the text (amber is never a text color).

export function Dot({ color, className = '' }) {
  return <span aria-hidden="true" className={`inline-block h-2 w-2 shrink-0 rounded-sm ${className}`} style={{ background: color }} />;
}

export function SeverityWord({ severity }) {
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-semibold text-ipc-ink" data-severity={severity}>
      <Dot color={SEVERITY_DOT[severity] ?? SEVERITY_DOT.info} />
      {SEVERITY_WORD[severity] ?? severity}
    </span>
  );
}

export function StatusWord({ status }) {
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap font-semibold text-ipc-ink" data-status={status}>
      <Dot color={STATUS_DOT[status] ?? STATUS_DOT.skip} />
      {STATUS_WORD[status] ?? status}
    </span>
  );
}

/** The mapping is a draft (served once per run): one note where references show, not one per finding. */
export const DraftMappingContext = createContext(false);

function RefGroup({ label, items }) {
  if (items.length === 0) return null;
  return (
    <div role="group" aria-label={label} className="flex flex-wrap items-center gap-1.5">
      <span className="text-xs font-semibold uppercase tracking-wide text-ipc-muted">{label}</span>
      {items.map((r) => (
        <span
          key={`${r.framework}|${r.version}|${r.controlId}`}
          title={`${REF_FRAMEWORK_NAMES[r.framework] ?? r.framework} ${r.version ?? ''}`.trim()}
          className="rounded border border-ipc-border px-1.5 text-[11.5px] font-medium text-ipc-muted"
        >
          {refLabel(r)}
        </span>
      ))}
    </div>
  );
}

/** Benchmark controls the check evaluates (exact fit) and informs (supporting fit), ids only, from the shipped mapping. */
export function ControlRefs({ refs }) {
  const draft = useContext(DraftMappingContext);
  const evaluates = refList(refs?.evaluates);
  const informs = refList(refs?.informs);
  if (evaluates.length === 0 && informs.length === 0) return null;
  return (
    <div className="space-y-1.5">
      <RefGroup label="Evaluates" items={evaluates} />
      <RefGroup label="Informs" items={informs} />
      {draft ? (
        <p className="inline-flex items-center gap-2 text-xs font-semibold text-ipc-ink">
          <Dot color="var(--warn-dot)" />Draft mapping
        </p>
      ) : null}
    </div>
  );
}

/** Up to two compact control labels for a list row ("NIST IA-2"), evaluates first. */
export function compactRefs(finding, n = 2) {
  return refList(finding?.controlRefs?.evaluates).slice(0, n).map(refLabel);
}
